import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi } from "../../lib/portalApi";
import { toast } from "sonner";
import { Trash2, ImagePlus, FolderOpen } from "lucide-react";

const EMPTY = { folder: "branding", filename: "", alt_text: "", url: "", notes: "" };

export default function MediaTab() {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const r = await adminApi.mediaList(filter || undefined);
    setAssets(r.assets || []);
    setFolders(r.folders || []);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    try {
      await adminApi.mediaCreate(form);
      toast.success("Media asset added");
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await adminApi.mediaDelete(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-media-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filter || "__all"}
          onValueChange={v => setFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream w-64" data-testid="media-folder-filter">
            <SelectValue placeholder="All folders" />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
            <SelectItem value="__all">All folders</SelectItem>
            {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => { setForm(EMPTY); setShowForm(!showForm); }} className="btn-gold h-10 px-5 text-xs ml-auto"
          data-testid="media-new-btn">
          <ImagePlus size={14} className="mr-2" /> Register Asset
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} className="glass p-5 grid md:grid-cols-2 gap-3" data-testid="media-form">
          <Select value={form.folder} onValueChange={v => setForm({ ...form, folder: v })}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="media-folder">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input required placeholder="Filename (e.g. cyber_hero.png)" value={form.filename}
            onChange={e => setForm({ ...form, filename: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" data-testid="media-filename" />
          <Input required placeholder="Alt text (accessibility)" value={form.alt_text}
            onChange={e => setForm({ ...form, alt_text: e.target.value })}
            className="bg-navy-deep border-gold-dim/40 md:col-span-2" data-testid="media-alt" />
          <Input required placeholder="URL (https://…)" value={form.url}
            onChange={e => setForm({ ...form, url: e.target.value })}
            className="bg-navy-deep border-gold-dim/40 md:col-span-2" data-testid="media-url" />
          <Textarea rows={2} placeholder="Notes (optional)" value={form.notes || ""}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button type="button" onClick={() => setShowForm(false)} className="btn-ghost-gold h-10 px-4 text-xs">Cancel</Button>
            <Button type="submit" className="btn-gold h-10 px-5 text-xs" data-testid="media-save-btn">Save</Button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="media-grid">
        {assets.map(a => (
          <article key={a.id} className="glass p-4 space-y-2" data-testid={`media-card-${a.id}`}>
            <div className="aspect-video bg-navy-deep rounded border border-gold-dim/20 flex items-center justify-center overflow-hidden">
              {a.url ? (
                <img src={a.url} alt={a.alt_text}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="w-full h-full object-cover" />
              ) : (
                <FolderOpen size={32} className="text-steel" />
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{a.folder}</div>
              <div className="text-cream text-sm truncate">{a.filename}</div>
              <div className="text-cream-dim text-xs truncate italic">{a.alt_text}</div>
            </div>
            <div className="flex justify-between items-center pt-1 border-t border-gold-dim/15">
              <a href={a.url} target="_blank" rel="noreferrer" className="text-electric text-xs hover:text-gold">Open ↗</a>
              <button onClick={() => del(a.id, a.filename)} className="text-[color:var(--auditiq-critical)] hover:text-gold"
                data-testid={`media-delete-${a.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </article>
        ))}
        {!assets.length && <div className="glass p-8 text-center text-cream-dim italic md:col-span-3">No assets registered.</div>}
      </div>
    </div>
  );
}
